import { useState } from "react";
import { ManaCost } from "@/components/common/ManaCost";
import { ManaText } from "@/components/common/ManaSymbol";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ZONES, addEntry, officialToSummary, toPayload } from "@/lib/decks";
import { EmptyState, PageHeader } from "@/components/common/PageParts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function AddToDeck({ card }) {
  const { data: decks = [] } = useQuery({ queryKey: ["decks", "mine"], queryFn: () => api.get("/me/decks").then((r) => r.data.items) });
  const eligible = decks.filter((d) => d.card_pool !== "custom_only");
  const [deckId, setDeckId] = useState("");
  const [zone, setZone] = useState("main");
  const add = async () => {
    try {
      const { data: deck } = await api.get(`/decks/${deckId}`);
      await api.patch(`/decks/${deckId}`, { entries: toPayload(addEntry(deck.entries, officialToSummary(card), zone)) });
      toast.success(`${card.name} added to ${deck.name}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  if (!eligible.length) return <p className="text-xs text-slate-500">Create a hybrid or official-only deck in the Deck Builder to add this card.</p>;
  return (
    <div className="flex flex-wrap gap-2" data-testid="official-add-to-deck">
      <select className="field w-auto flex-1 text-xs" value={deckId} onChange={(e) => setDeckId(e.target.value)} aria-label="Deck" data-testid="official-deck-select">
        <option value="">Choose a deck…</option>
        {eligible.map((d) => <option key={d.deck_id} value={d.deck_id}>{d.name}</option>)}
      </select>
      <select className="field w-auto text-xs" value={zone} onChange={(e) => setZone(e.target.value)} aria-label="Zone" data-testid="official-zone-select">
        {ZONES.map((z) => <option key={z.key} value={z.key}>{z.label}</option>)}
      </select>
      <button className="btn-gold px-4 py-2 text-xs" disabled={!deckId} onClick={add} data-testid="official-add-button">Add to deck</button>
    </div>
  );
}

function CardDialog({ card, onClose }) {
  const { user } = useAuth();
  return (
    <Dialog open={Boolean(card)} onOpenChange={(o) => !o && onClose()}>
      {card && (
        <DialogContent className="max-w-3xl border-[#2A3447] bg-[#101828] text-slate-200" data-testid="official-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-100">{card.name}</DialogTitle>
            <DialogDescription>{card.set_name} · #{card.collector_number} · <span className="capitalize">{card.rarity}</span> · Illustrated by {card.artist}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">{card.faces.filter((f) => f.image_url).map((f) => <img key={f.name} src={f.image_url} alt={f.name} className="w-full rounded-xl" />)}</div>
            <div className="space-y-4 text-sm">
              {card.faces.map((f) => (
                <div key={f.name} className="panel p-4">
                  <p className="font-semibold text-amber-100">{f.name} <ManaCost cost={f.mana_cost} /></p>
                  <p className="mt-1 text-xs text-slate-400">{f.type_line}</p>
                  {f.oracle_text && <p className="mt-2 whitespace-pre-line text-slate-200"><ManaText text={f.oracle_text} /></p>}
                </div>
              ))}
              <a href={card.scryfall_uri} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-aether-300 hover:underline" data-testid="official-scryfall-link">View on Scryfall<ExternalLink className="h-3 w-3" /></a>
              {user && <AddToDeck card={card} />}
            </div>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}

export default function OfficialCards() {
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const { data, isFetching, error } = useQuery({ queryKey: ["official", q, page], enabled: q.length > 1, retry: false,
    queryFn: () => api.get("/official/cards/search", { params: { q, page } }).then((r) => r.data) });
  return (
    <div className="container-x py-12">
      <PageHeader eyebrow="Powered by the Scryfall API" title="Official Cards" testid="official-header"
        description="Search every official Magic: The Gathering card. Official records are kept entirely separate from community custom cards." />
      <form onSubmit={(e) => { e.preventDefault(); setQ(input.trim()); setPage(1); }} className="mb-8 flex max-w-2xl gap-3">
        <input className="field" placeholder='Try "lightning bolt", t:dragon c:r, or o:"draw a card"' value={input} onChange={(e) => setInput(e.target.value)} aria-label="Search official cards" data-testid="official-search-input" />
        <button className="btn-gold" data-testid="official-search-button">{isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Search</button>
      </form>
      {error && <p className="mb-6 text-sm text-red-300" data-testid="official-error">{formatApiError(error)}</p>}
      {!q && <EmptyState icon={Search} title="Search the official multiverse" testid="official-idle">Uses Scryfall search syntax.</EmptyState>}
      {data && (
        <>
          <p className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="official-count">{data.total} cards</p>
          {data.items.length ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="official-grid">
              {data.items.map((c) => (
                <button key={c.scryfall_id} onClick={() => setSelected(c)} className="text-left" data-testid={`official-card-${c.scryfall_id}`}>
                  <div className="card-frame aspect-[63/88]">{c.image_url ? <img src={c.image_url} alt={c.name} className="h-full w-full object-cover" loading="lazy" /> : <p className="p-3 text-xs">{c.name}</p>}</div>
                  <p className="mt-2 line-clamp-1 text-sm text-slate-100">{c.name}</p>
                  <p className="text-[11px] text-slate-500">{c.set_name}</p>
                </button>
              ))}
            </div>
          ) : <EmptyState title="No official cards match" testid="official-empty" />}
          <div className="mt-8 flex justify-center gap-3">
            <button className="btn-ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="official-prev">Prev</button>
            <button className="btn-ghost" disabled={!data.has_more} onClick={() => setPage(page + 1)} data-testid="official-next">Next</button>
          </div>
        </>
      )}
      <p className="mt-12 text-xs text-slate-500" data-testid="scryfall-attribution">Card data and images provided by <a href="https://scryfall.com" target="_blank" rel="noreferrer" className="text-aether-300 hover:underline">Scryfall</a>. Magic: The Gathering and card images are © Wizards of the Coast. Not affiliated with or endorsed by Scryfall or Wizards.</p>
      <CardDialog card={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
